import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSurveyOverview } from './useSurveyOverview';
import { getTopicbaseRequest, ITopicbaseCategory } from '../services/topicbase';

export interface ITopicItem {
  code: string;
  id: string;
  order: number;
  estimatedResponseTime: number;
  text: Record<string, string>;
}

export interface ITopic {
  code: string;
  id: string;
  numberOfItems: number;
  order: number;
  title: Record<string, string>;
  shortDescription: Record<string, string>;
  items: ITopicItem[];
}

export interface IGenerateContentsArray {
  code: string;
  description: string;
  id: string;
  name: string;
  numberOfItems: number;
  numberOfTopics: number;
  order: number;
  title: Record<string, string>;
  topics: ITopic[];
  expanded?: boolean;
}

export interface IStaticData {
  introText: string | null;
  outroText: string | null;
  helpText: string | null;
}

export const useSurveyContents = (surveyType: string, surveyId: string, orgName: string) => {
  const [topicsBase, setTopics] = useState<ITopicbaseCategory[]>([]);
  const [categories, setCategories] = useState<{ code: string; topics: { code: string }[] }[]>([]);
  const [openQuestions, setOpenQuestions] = useState<{ code: string }[]>();
  const [staticData, setStaticData] = useState<IStaticData>({
    introText: null,
    outroText: null,
    helpText: null,
  });

  const [loading, setLoading] = useState(false);
  const [hellAndEndInitial, setHellAndEndInitial] = useState<Record<string, Record<string, string> | null>>({});
  const [linkToPreview, setLinkToPreview] = useState('');

  const {
    i18n: { language },
  } = useTranslation();
  const { getSurveyContents } = useSurveyOverview();
  const getLocalizedValue = (value: unknown): string => {
    if (value && typeof value === 'object') {
      return (value as Record<string, string>)[language] || '';
    }
    return '';
  };

  const generateContentsArray = useMemo(
    () =>
      topicsBase?.length > 0
        ? topicsBase.map(base => {
          const selectedCategories = categories.map(({ code }) => code) ?? [];

          const topics = base?.topics.map(topic => {
            const index = categories.findIndex(({ code }) => code === base?.code);
            const selectedTopics =
                categories[index]?.topics.map(({ code }: { code: string }) => code) ?? [];

            const items = topic?.items.map(item => ({
              ...item,
              id: `${item?.code}+${item?.order}`,
              name: getLocalizedValue(item?.text),
            }));

            return {
              ...topic,
              items,
              id: `${getLocalizedValue(topic?.title)}+${topic?.code}`,
              name: getLocalizedValue(topic?.title),
              shortDescription: getLocalizedValue(topic?.shortDescription),
              selected: selectedTopics?.includes(topic?.code) ?? false,
            };
          });

          return {
            ...base,
            topics,
            id: `${getLocalizedValue(base?.title)}+${base?.code}`,
            name: getLocalizedValue(base?.title),
            description: getLocalizedValue(base?.description),
            selected: selectedCategories.includes(base?.code) ?? false,
          };
        })
        : [],
    [categories, language, topicsBase]
  );

  const transformPlaceholders = (text: string) => {
    return text.replaceAll('[[Organization Name]]', orgName);
  };
  const textReplace = useMemo(() => {
    const { introText, outroText, helpText } = staticData;

    return {
      introText: introText && transformPlaceholders(introText),
      outroText: outroText && transformPlaceholders(outroText),
      helpText: helpText && transformPlaceholders(helpText),
    };
  }, [staticData]);

  useEffect(() => {
    (async () => {
      if (!surveyType) return;

      try {
        setLoading(true);

        const surveyCategoriesReq = await getSurveyContents(surveyId);

        const { introText, outroText, title, helpText, preview_link, openQuestions } =
          surveyCategoriesReq;

        setHellAndEndInitial({ introText, outroText, title, helpText });
        setStaticData({
          introText: introText && introText[language],
          outroText: outroText && outroText[language],
          helpText: helpText && helpText[language],
        });
        const surveyCategories = surveyCategoriesReq.scope;

        setCategories(surveyCategories);

        const topicbase = await getTopicbaseRequest(surveyType, language);

        const topicsBaseArr = topicbase?.categories || [];
        setOpenQuestions(openQuestions);
        setTopics(topicsBaseArr);
        setLinkToPreview(preview_link || '');
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    })();
  }, [language, surveyId, surveyType]);

  return {
    generateContentsArray,
    language,
    loading,
    staticData: textReplace,
    hellAndEndInitial,
    linkToPreview,
    openQuestions,
  };
};
